'use client';

import { FC, useEffect, useRef } from 'react';

import { Container } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { JQueryGlobalModel } from '@telialt/models/src/JQueryGlobalModel';

import { BlockSection } from '../../../common/Common.styles';
import {
    EXCAVATION_PERMIT_IFRAME_ID,
    EXCAVATION_PERMIT_IFRAME_SELECTOR,
    EXCAVATION_PERMIT_IFRAME_SRC,
    JOTFORM_EMBED_HANDLER_SCRIPT_ID,
    JOTFORM_EMBED_HANDLER_SCRIPT_SRC,
} from './constants';
import { ExcavationPermitIframe } from './ExcavationPermit.styles';

declare global {
    interface Window extends JQueryGlobalModel {
        jotformEmbedHandler?: (iframeSelector: string, baseUrl: string) => void;
    }
}

const MIN_HEIGHT_DROP_PX = 1000;

const getIframe = () => document.querySelector<HTMLIFrameElement>(EXCAVATION_PERMIT_IFRAME_SELECTOR);

export const ExcavationPermit: FC = () => {
    const { t } = useTranslations();
    const previousHeightRef = useRef<number | null>(null);

    useEffect(() => {
        const initializeJotform = () => {
            window.jotformEmbedHandler?.(EXCAVATION_PERMIT_IFRAME_SELECTOR, 'https://form.jotform.com/');
        };

        if (document.getElementById(JOTFORM_EMBED_HANDLER_SCRIPT_ID)) {
            initializeJotform();
        } else {
            const script = document.createElement('script');
            script.id = JOTFORM_EMBED_HANDLER_SCRIPT_ID;
            script.src = JOTFORM_EMBED_HANDLER_SCRIPT_SRC;
            script.onload = initializeJotform;
            document.body.appendChild(script);
        }
    }, []);

    useEffect(() => {
        const scrollOnRapidDrop = () => {
            const styleHeight = Number.parseFloat(getIframe().style?.height);
            const height = Number.isFinite(styleHeight) ? styleHeight : null;

            if (height === null) {
                return;
            }
            const previousHeight = previousHeightRef.current;
            const isRapidDrop = previousHeight !== null && previousHeight - height >= MIN_HEIGHT_DROP_PX;
            if (isRapidDrop) {
                getIframe().scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            previousHeightRef.current = height;
        };

        const listenToJotformMessages = (event: MessageEvent) => {
            const isJotformEvent = event.origin.includes('jotform.com');
            if (isJotformEvent && typeof event?.data === 'string' && event.data.includes('setHeight')) {
                scrollOnRapidDrop();
            }
        };
        window.addEventListener('message', listenToJotformMessages);

        return () => {
            window.removeEventListener('message', listenToJotformMessages);
        };
    }, []);

    return (
        <Container>
            <BlockSection>
                <ExcavationPermitIframe
                    id={EXCAVATION_PERMIT_IFRAME_ID}
                    title={t['content.block_widget.excavation_permit.title']}
                    src={EXCAVATION_PERMIT_IFRAME_SRC}
                    allow="geolocation; microphone; camera; fullscreen; payment"
                />
            </BlockSection>
        </Container>
    );
};
